import { StrapiPages } from "@lib/strapi/pages";
import { StrapiResponse } from "@lib/strapi/dtos/core/response.dto";
import { StrapiRoutes } from "@lib/strapi/routes";
import {
  StrapiTeamMemberDTO,
  TeamMemberDTO,
} from "@lib/strapi/dtos/content-types/teamMember.dto";
import { fetchAPI } from "@lib/strapi/fetch-api";
import { getImageProps, ImageProps } from "@lib/image";
import { parseStrapiContent } from "@lib/strapi/content";

/** first markdown paragraph → plain text: the bios open with a one-paragraph
 *  specialty summary (owner-reviewed), which is exactly the card teaser — a
 *  separate CMS field would drift from the bio on every edit */
const teaserOf = (markdown?: string): string => {
  const firstParagraph = (markdown ?? "").split(/\n\s*\n/)[0] ?? "";
  return firstParagraph
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/[*_`#>]/g, "")
    .trim();
};

export class TeamMemberService {
  static async fromStrapi(
    dto: TeamMemberDTO,
    locale: string,
    strapiData?: StrapiTeamMemberDTO,
  ): Promise<TeamMemberDTO> {
    if (!strapiData) {
      return null;
    }
    if (dto == null) {
      dto = new TeamMemberDTO();
    }
    dto.displayName = strapiData.displayName;
    dto.slug = strapiData.slug;
    dto.qualification = strapiData.qualification;
    dto.role = strapiData.role;
    dto.bio = await parseStrapiContent(strapiData.bio);
    dto.bioTeaser = teaserOf(strapiData.bio);
    dto.photo = getImageProps(strapiData.photo);
    dto.sortIndex = strapiData.sortIndex;
    dto.isHeadOfPractice = strapiData.isHeadOfPractice;
    // meta pipeline (P8q): the seo component override, and the bio's first
    // paragraph as the description fallback (compileEntitySEOInformation
    // reads `teaser`) — profile pages were all sharing the site default
    dto.seo = (strapiData as { seo?: TeamMemberDTO["seo"] }).seo;
    dto.teaser = dto.bioTeaser;
    return dto;
  }

  static async fetchAll(
    authToken: string,
    locale: string,
  ): Promise<TeamMemberDTO[]> {
    const token = (process.env.CMS_API_TOKEN || process.env.STRAPI_API_TOKEN);
    const urlParamsObject = {
      locale,
      sort: ["sortIndex:asc"],
      pagination: { pageSize: 200 },
    };
    const response: StrapiResponse<StrapiTeamMemberDTO[]> = await fetchAPI(
      StrapiRoutes.teamMembers(),
      urlParamsObject,
      token,
    );
    const dto = new TeamMemberDTO();
    return await Promise.all(
      (response.data ?? []).map(
        async (item) =>
          await TeamMemberService.fromStrapi(new TeamMemberDTO(), locale, item),
      ),
    );
  }

  static async fetchBySlug(
    authToken: string,
    locale: string,
    slug?: string,
  ): Promise<TeamMemberDTO> {
    const token = (process.env.CMS_API_TOKEN || process.env.STRAPI_API_TOKEN);
    const urlParamsObject = { locale, filters: { slug: slug } };
    const response: StrapiResponse<StrapiTeamMemberDTO[]> = await fetchAPI(
      StrapiRoutes.teamMembers(),
      urlParamsObject,
      token,
    );
    const dto = new TeamMemberDTO();
    if ((response.data ?? []).length === 0) {
      return null;
    }
    return await TeamMemberService.fromStrapi(
      dto,
      locale,
      (response.data ?? [])[0],
    );
  }
}
